import {
  Body2,
  Box,
  Color,
  FontFamily,
  FontWeight,
  Heading2,
  TextAlign,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'
import { CSSObject } from '@emotion/react'
import React from 'react'

import { CWCLinkButton } from '../CWCLinkButton/CWCLinkButton'
import { LandingPageContentSections, TrackingActions, trackCTAClick } from '../../tracking'
import { CWCCompetitorsComparisonTable } from './components/CWCCompetitorsComparisonTable/CWCCompetitorsComparisonTable'
import { CWC_PRICE_COMPARISON_DIMENSIONS, CWC_PRICE_COMPARISON_SECTION } from './config'

// Responsive bands map to [base(<640), 640, 768 (iPad), 1024 (desktop)] — the CwC 4-value convention
// (a 3-value array lumps every width >=768 onto the tablet value). Numeric px values are Figma-current;
// the figma-fidelity-loop reconciles them against the prototype (the Figma is a lossy capture).

// Dark section band (#333D47 = ColdGrey_800). Vertical padding lives here so the band extends fully.
const SectionContainer = withCitrusTheme('section', (theme) => [
  { backgroundColor: theme.color(Color.ColdGrey_800), boxSizing: 'border-box' } as CSSObject,
  theme.responsiveStyle(['56px', '56px', '56px', '80px'], ['paddingTop']) as CSSObject,
  theme.responsiveStyle(['56px', '56px', '56px', '80px'], ['paddingBottom']) as CSSObject,
])

// Inner row: capped + centred, laid out with CSS grid + named areas at every breakpoint. DOM order is
// content → table → CTA, which is also the reading/focus order (so screen-reader and tab order match
// the stacked visual order and the CTA is never announced before the table it follows). Grid (not a
// flex column) also avoids the <table>-in-flex max-content overflow trap on narrow viewports.
//   base/iPad: single column — content, table, cta (CTA at the bottom, note 629:1499).
//   desktop:   two columns — copy + cta grouped in the left column, table spanning the right. The two
//              1fr spacer rows above/below the copy+cta pair centre that group vertically against the
//              taller table without needing a wrapper element (which would force cta adjacent to
//              content in the DOM and break the reading order above). The table track is a FIXED 535px
//              (its design width), not a fluid fraction: a fractional track let the table shrink below
//              535px in the 1024–1100 range, scaling every column with the viewport and pinching the
//              Gousto column until the "Best value" pill overflowed it. The copy column takes the rest.
const SectionRow = withCitrusTheme(Box, (theme) => [
  {
    boxSizing: 'border-box',
    maxWidth: '1100px',
    width: '100%',
    margin: '0 auto',
    display: 'grid',
    gridTemplateColumns: '1fr',
    gridTemplateAreas: '"content" "table" "cta"',
    justifyItems: 'center',
    rowGap: '24px',
  } as CSSObject,
  theme.responsiveStyle(['16px', '16px', '22px', '40px'], ['paddingLeft']) as CSSObject,
  theme.responsiveStyle(['16px', '16px', '22px', '40px'], ['paddingRight']) as CSSObject,
  {
    [`@media (min-width: ${theme.tokens.breakpoints[2]})`]: {
      gridTemplateColumns: 'minmax(0, 1fr) 535px',
      gridTemplateRows: '1fr min-content min-content 1fr',
      gridTemplateAreas: '"cornerTop table" "content table" "cta table" "cornerBottom table"',
      justifyItems: 'start',
      columnGap: '48px',
    },
  } as CSSObject,
])

// Content column (heading + copy only — CTA is a sibling). Children STRETCH to the column width so
// each element's own `textAlign` controls its alignment. This is deliberate: Citrus text emits
// `text-align: initial` (= start/left, and it does NOT inherit) when no `textAlign` prop is set, so
// alignment can't be driven from this container — Heading2 and Body2 each set their own responsive
// textAlign. Alignment per breakpoint (design sign-off): mobile left, iPad centred, desktop
// left (see the Heading2/Body2 textAlign arrays below).
const ContentColumn = withCitrusTheme(Box, (theme) => [
  {
    gridArea: 'content',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: '16px',
    maxWidth: '420px',
    width: '100%',
  } as CSSObject,
  // iPad: widen so the heading sits on one line + body wraps as in Figma (the 420 cap is mobile-only).
  {
    [`@media (min-width: ${theme.tokens.breakpoints[1]})`]: { maxWidth: '620px' },
  } as CSSObject,
  // Desktop: back to a ~420 copy column in the grid's left cell.
  {
    [`@media (min-width: ${theme.tokens.breakpoints[2]})`]: { maxWidth: '420px' },
  } as CSSObject,
])

// CTA grid item. Full-width mobile (matches the copy column's 420 cap); left-aligned on desktop.
const CtaWrap = withCitrusTheme(Box, (theme) => [
  {
    gridArea: 'cta',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    maxWidth: '420px',
  } as CSSObject,
  {
    [`@media (min-width: ${theme.tokens.breakpoints[2]})`]: {
      justifyContent: 'flex-start',
      maxWidth: 'none',
    },
  } as CSSObject,
])

// Table wrapper — a plain BLOCK that fills the grid area and gives the <table> a definite containing
// block. Must NOT be a flex container: a <table> that is a direct flex item takes its automatic
// minimum size from its content (min-width:auto), so on narrow viewports (~320px) the unbreakable
// brand names + pill + cell padding can push it past the available width. As a block child the table
// honours width:100%/maxWidth and centres via its own `margin: 0 auto` (below).
const TableWrap = withCitrusTheme(Box, () => ({
  gridArea: 'table',
  width: '100%',
  minWidth: 0,
}))

// Footnote marker (‡) in the supporting copy — small raised superscript, not full body size.
const FootnoteMark = withCitrusTheme('sup', () => ({
  fontSize: '0.64em',
  verticalAlign: 'super',
  lineHeight: 0,
}))

export function CWCPriceComparisonSection() {
  const { heading, body, brands, cta } = CWC_PRICE_COMPARISON_SECTION

  const trackCTA = () => {
    trackCTAClick(TrackingActions.BOX_PRICING_NAVIGATION, LandingPageContentSections.VALUE)
  }

  return (
    <SectionContainer data-testid="cwcPriceComparisonSection">
      {/* DOM order is content → table → cta so reading/focus order matches the stacked visual order
          (the CTA follows the table it refers to). Desktop grid areas re-place the cta into the left
          column visually without changing this source order. */}
      <SectionRow>
        <ContentColumn>
          {/* Alignment per breakpoint [base, 640, 768(iPad), 1024(desktop)]: mobile left, iPad
              centred, desktop left (design sign-off). Heading + body share the same pattern. */}
          <Heading2
            size={13}
            color={Color.White}
            fontFamily={FontFamily.ExtraBold}
            fontWeight={FontWeight.Bold}
            textAlign={[TextAlign.Left, null, TextAlign.Center, TextAlign.Left]}
          >
            {heading}
          </Heading2>
          <Body2
            color={Color.White}
            textAlign={[TextAlign.Left, null, TextAlign.Center, TextAlign.Left]}
          >
            {body}
            <FootnoteMark>‡</FootnoteMark>
          </Body2>
        </ContentColumn>

        <TableWrap>
          <CWCCompetitorsComparisonTable
            brands={brands}
            dimensions={CWC_PRICE_COMPARISON_DIMENSIONS}
          />
        </TableWrap>

        <CtaWrap>
          <CWCLinkButton href={cta.href} onClick={trackCTA} variant="secondary" colorMode="dark">
            {cta.label}
          </CWCLinkButton>
        </CtaWrap>
      </SectionRow>
    </SectionContainer>
  )
}
